import { useEffect, useState } from 'react'
import WeekPlanner from './WeekPlanner.jsx'
import MobilePlanner from './MobilePlanner.jsx'
import { useAuth } from '../../contexts/AuthContext.jsx'
import { usePlannerState } from '../../hooks/usePlannerState.js'
import { useMediaQuery } from '../../hooks/useMediaQuery.js'
import PlannerGuestPreview from './PlannerGuestPreview.jsx'
import PlannerSkeleton, { PlannerErrorState } from './PlannerSkeleton.jsx'
import {
  clearPlanHighlightFromUrl,
  readPlanHighlight,
} from '../../utils/appNavigation.js'

function PlannerPanelAuthenticated({
  onSwitchToRecipes,
  onSwitchToShop,
  onImportRecipe,
  onViewRecipe,
  onOpenMainChat,
  onSavePrompt,
  saveAskDismissed = false,
  guestMode = false,
  onboardingMode = false,
}) {
  const {
    plannerState,
    libraryCount,
    weekLabel,
    preferences,
    loading,
    error,
    livePlannerBanner,
    dismissLivePlannerBanner,
    starterPacks,
    addedPackIds,
    onApplyStarterPack,
    livePlannerApi,
    weekNav,
    reloadPlanner,
    ensureAssignedIngredientLines,
    retryAssignedIngredientLines,
    assignedIngredientsLoading,
    assignedIngredientsError,
  } = usePlannerState({ onSwitchToShop, onboardingMode })
  const isMobileLayout = useMediaQuery('(max-width: 768px)')
  const [highlightRecipeId, setHighlightRecipeId] = useState(() => readPlanHighlight())

  useEffect(() => {
    if (loading || !livePlannerApi) return undefined
    const id = readPlanHighlight()
    if (!id) return undefined

    setHighlightRecipeId(id)
    const scrollTimer = window.setTimeout(() => {
      document
        .querySelector('[data-plan-highlight="true"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 120)
    const clearTimer = window.setTimeout(() => {
      clearPlanHighlightFromUrl()
      setHighlightRecipeId(null)
    }, 4500)

    return () => {
      window.clearTimeout(scrollTimer)
      window.clearTimeout(clearTimer)
    }
  }, [loading, livePlannerApi, livePlannerApi?.suggestionMeals, livePlannerApi?.assignedMeals])

  if (loading) {
    return <PlannerSkeleton onboardingMode={onboardingMode} />
  }

  if (error || !livePlannerApi) {
    return <PlannerErrorState onRetry={reloadPlanner} onboardingMode={onboardingMode} />
  }

  const plannerProps = {
    plannerState,
    dietary: preferences,
    weekLabel,
    weekNav,
    libraryCount,
    onSwitchToRecipes,
    onImportRecipe,
    livePlannerApi,
    liveBanner: livePlannerBanner,
    onDismissLiveBanner: dismissLivePlannerBanner,
    starterPacks,
    addedPackIds,
    onApplyStarterPack,
    onViewRecipe,
    onOpenMainChat,
    onSavePrompt,
    saveAskDismissed,
    highlightRecipeId,
    guestMode,
    onboardingMode,
    ensureAssignedIngredientLines,
    retryAssignedIngredientLines,
    assignedIngredientsLoading,
    assignedIngredientsError,
  }

  return isMobileLayout ? (
    <MobilePlanner {...plannerProps} isMobile />
  ) : (
    <WeekPlanner {...plannerProps} />
  )
}

export default function PlannerPanel({
  activationWelcomeCopy = null,
  activationWelcomeVariant = 'a',
  onSetupKitchen,
  guestMode = false,
  onboardingMode = false,
  onSavePrompt,
  saveAskDismissed = false,
  ...props
}) {
  const { isAuthenticated, isLoading: authLoading } = useAuth()

  if (authLoading) {
    return <PlannerSkeleton onboardingMode={onboardingMode} />
  }

  if (!isAuthenticated && !guestMode) {
    return (
      <PlannerGuestPreview
        welcomeCopy={activationWelcomeCopy}
        welcomeVariant={activationWelcomeVariant}
        onSetupKitchen={onSetupKitchen}
      />
    )
  }

  return (
    <PlannerPanelAuthenticated
      {...props}
      guestMode={guestMode}
      onboardingMode={onboardingMode}
      onSavePrompt={onSavePrompt}
      saveAskDismissed={saveAskDismissed}
    />
  )
}
